import { apiServiceInstance } from '@/core/helpers/utils';
import { formatDatePayload } from '@/core/hooks/use-date-time-hook';
import type {
  BbgtKhoListQuery,
  BbgtVatTuSelectionQuery,
  DanhSachKhoBbgtResponse,
  DanhSachVatTuBbgtResponse,
  PhieuXuatKhoCreateRequest,
} from '@/types/phieu-xuat-kho';
import type {
  ICreateOrUpdatePhieuXuatKhac,
  IPhieuXuatPaginationFilterParams,
  IVatTuSelectedInKho,
} from '../interfaces/Models';
import {
  ApiResponse,
  ChenhLechVatTuBienBanResponse,
  IPhieuXuatKhoResponseBase,
  IVatTuTonKhoResponse,
  PaginationResponseBase,
  ReceiptBaseResponse,
} from './../interfaces/ApiResponses';

export default class PhieuXuatKhoService {
  static create(payload: PhieuXuatKhoCreateRequest): Promise<{
    data: ApiResponse<IPhieuXuatKhoResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      '/api/v1/phieu-xuat-kho/tao-moi',
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  static update(
    id_phieu_xuat: number,
    payload: PhieuXuatKhoCreateRequest
  ): Promise<{
    data: ApiResponse<IPhieuXuatKhoResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/phieu-xuat-kho/cap-nhat/${id_phieu_xuat}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  static createFromBBGT(
    id_bien_ban: number,
    so_bien_ban: string,
    ten_nguoi_nhan: string,
    selectedSuppliesInWarehouse: IVatTuSelectedInKho[],
    type: 1 | 2 = 1
  ): Promise<{
    data: ApiResponse<
      {
        id: number;
        ten_nguoi_nhan: string | null;
        so_bien_ban: string;
        so_phieu: string;
      }[]
    >;
  }> {
    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      'Content-Type'
    ] = 'multipart/form-data';

    const bodyFormData = new FormData();
    bodyFormData.append('phieuXuatKho[so_bien_ban]', so_bien_ban);
    bodyFormData.append('phieuXuatKho[loai_phieu]', '0'); // 0 là phiếu xuất kho từ biên bản giải thể

    bodyFormData.append('phieuXuatKho[ten_nguoi_nhan]', ten_nguoi_nhan);

    bodyFormData.append(
      'phieuXuatKho[type]', // 1 là trong định mức, 2 là ngoài ĐM
      type.toString()
    );

    selectedSuppliesInWarehouse.forEach((item) => {
      item.kho_arr.forEach((w) => {
        bodyFormData.append(
          `phieuXuatKho[vatTuKho][${w.kho_id}][${item.id_vat_tu}][so_luong]`,
          w.da_lay.toString()
        );

        if (w.sl_tai_sd !== undefined) {
          bodyFormData.append(
            `phieuXuatKho[vatTuKho][${w.kho_id}][${item.id_vat_tu}][sl_tai_sd]`,
            w.sl_tai_sd.toString()
          );
        }

        if (w.sl_mat !== undefined) {
          bodyFormData.append(
            `phieuXuatKho[vatTuKho][${w.kho_id}][${item.id_vat_tu}][sl_mat]`,
            w.sl_mat.toString()
          );
        }

        if (w.sl_phe_lieu !== undefined) {
          bodyFormData.append(
            `phieuXuatKho[vatTuKho][${w.kho_id}][${item.id_vat_tu}][sl_phe_lieu]`,
            w.sl_phe_lieu.toString()
          );
        }

        if (w.kg_phe_lieu !== undefined) {
          bodyFormData.append(
            `phieuXuatKho[vatTuKho][${w.kho_id}][${item.id_vat_tu}][kg_phe_lieu]`,
            w.kg_phe_lieu.toString()
          );
        }
      });
    });

    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/phieu-xuat-kho/bien-ban-giai-the/${id_bien_ban}/tao-moi`,
      bodyFormData
    );
  }

  static createOrUpdatePhieuXuatKhac(
    params: ICreateOrUpdatePhieuXuatKhac
  ): Promise<{
    data: ApiResponse<IPhieuXuatKhoResponseBase>;
  }> {
    const {
      ten_nguoi_nhan,
      id_phieu_xuat,
      id_kho,
      ly_do,
      ma_ct_no,
      ma_tk_no,
      ngay,
      so_bien_ban,
      ten_tk_no,
      vat_tu_items,
      loai_phieu,
    } = params;
    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      'Content-Type'
    ] = 'multipart/form-data';

    const bodyFormData = new FormData();

    const isEditing = !!id_phieu_xuat;

    bodyFormData.append('phieuXuat[so_bien_ban]', so_bien_ban);
    bodyFormData.append('phieuXuat[ten_nguoi_nhan]', ten_nguoi_nhan);
    bodyFormData.append('phieuXuat[ly_do]', ly_do);
    bodyFormData.append('phieuXuat[ngay]', formatDatePayload(ngay));
    bodyFormData.append('phieuXuat[loai_phieu]', loai_phieu.toString());

    vat_tu_items.forEach((item, index) => {
      const idVatTu = item.id ?? item.pivot?.id_vat_tu;

      if (!idVatTu) {
        return;
      }

      const {
        don_gia,
        so_luong,
        ghi_chu,
        sl_tai_sd,
        sl_mat,
        sl_phe_lieu,
        kg_phe_lieu,
      } = item.pivot ?? {};

      bodyFormData.append(`vatTu[${index}][id_vat_tu]`, String(idVatTu));
      bodyFormData.append(`vatTu[${index}][so_luong]`, String(so_luong ?? 0));

      bodyFormData.append(
        `vatTu[${index}][don_gia]`,
        don_gia ? don_gia.toString() : '0'
      );

      if (loai_phieu === 2) {
        bodyFormData.append(
          `vatTu[${index}][ghi_chu]`,
          ghi_chu ? ghi_chu.toString() : ''
        );
      }

      bodyFormData.append(`vatTu[${index}][sl_tai_sd]`, String(sl_tai_sd ?? 0));
      bodyFormData.append(`vatTu[${index}][sl_mat]`, String(sl_mat ?? 0));
      bodyFormData.append(
        `vatTu[${index}][sl_phe_lieu]`,
        String(sl_phe_lieu ?? 0)
      );
      bodyFormData.append(
        `vatTu[${index}][kg_phe_lieu]`,
        String(kg_phe_lieu ?? 0)
      );
    });

    bodyFormData.append('phieuXuat[tk_no][ma_tk]', ma_tk_no);

    bodyFormData.append(
      'phieuXuat[tk_no][ten_tk]',
      ten_tk_no !== undefined && ten_tk_no !== null ? String(ten_tk_no) : ''
    );

    bodyFormData.append(
      'phieuXuat[tk_no][ma_ct]',
      ma_ct_no !== undefined && ma_ct_no !== null ? String(ma_ct_no) : ''
    );

    const uri = isEditing
      ? `/api/v1/phieu-xuat-kho/cap-nhat/${id_phieu_xuat}`
      : `/api/v1/phieu-xuat-kho/kho/${id_kho}/tao-moi`;

    return apiServiceInstance().vueInstance.axios.post(uri, bodyFormData);
  }

  static get(receiptId: number): Promise<{
    data: ApiResponse<ReceiptBaseResponse>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/phieu-xuat-kho/chi-tiet/${receiptId}`
    );
  }

  static getListByKhoId({ current_page, per_page, id_kho, search, loai_phieu, processing_status }: IPhieuXuatPaginationFilterParams): Promise<{
    data: ApiResponse<PaginationResponseBase<IPhieuXuatKhoResponseBase>>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/phieu-xuat-kho/danh-sach/kho/${id_kho}`,
      {
        params: {
          page: current_page ?? 1,
          ...(search && {
            s: search,
          }),
          ...(loai_phieu !== undefined && {
            loai_phieu,
          }),
          ...(processing_status !== undefined && {
            processing_status,
          }),
        },
        headers: {
          perpage: per_page ?? 1000,
        },
      }
    );
  }

  static updateNgayHoanThanh({
    id,
    ngay_hoan_thanh,
  }: {
    id: number;
    ngay_hoan_thanh: Date;
  }) {
    const dateStr = formatDatePayload(ngay_hoan_thanh);

    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      'Content-Type'
    ] = 'multipart/form-data';
    const bodyFormData = new FormData();

    bodyFormData.append('extra[ngay_hoan_thanh]', dateStr);

    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/phieu-xuat-kho/update-ngay-hoan-thanh/${id}`,
      bodyFormData
    );
  }

  static delete(id: number) {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/phieu-xuat-kho/xoa/${id}`
    );
  }

  static vaildateVatTuXuatKho({
    id,
    exclude_id_phieu_xuat_items,
  }: {
    id: number;
    exclude_id_phieu_xuat_items?: number[];
  }): Promise<{
    data: ApiResponse<ChenhLechVatTuBienBanResponse[]>;
  }> {
    let query = '';

    if (exclude_id_phieu_xuat_items?.length) {
      exclude_id_phieu_xuat_items.forEach((phieuXuatId) => {
        query = `${query}&exclude_id_phieu_xuat[]=${phieuXuatId}`;
      });
    }

    const baseUri = `/api/v1/phieu-xuat-kho/validate-dinh-muc-xuat-kho/${id}`;

    const uri = exclude_id_phieu_xuat_items?.length
      ? `${baseUri}?${query}`
      : baseUri;

    return apiServiceInstance().vueInstance.axios.get(uri);
  }

  static duyetPhieuXuat({
    id,
    ngay_hoan_thanh,
  }: {
    id: number;
    ngay_hoan_thanh: Date;
  }) {
    const dateStr = formatDatePayload(ngay_hoan_thanh);

    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      'Content-Type'
    ] = 'multipart/form-data';

    const bodyFormData = new FormData();
    bodyFormData.append('extra[ngay_hoan_thanh]', dateStr);

    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/phieu-xuat-kho/duyet/${id}`,
      bodyFormData
    );
  }

  static getListForApproval({
    current_page,
    per_page,
    search,
  }: {
    current_page?: number;
    per_page?: number;
    search?: string;
  }): Promise<{
    data: ApiResponse<PaginationResponseBase<IPhieuXuatKhoResponseBase>>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/phieu-xuat-kho/danh-sach/cho-duyet`,
      {
        params: {
          page: current_page ?? 1,
          ...(search && {
            s: search,
          }),
        },
        headers: {
          perpage: per_page ?? 50,
        },
      }
    );
  }

  static getTonKho(
    id_vat_tu: number,
    id_kho: number
  ): Promise<{
    data: ApiResponse<IVatTuTonKhoResponse>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/phieu-xuat-kho/ton-kho`,
      {
        params: {
          id_vat_tu,
          id_kho,
        },
      }
    );
  }

  static adminSign(id: number) {
    return apiServiceInstance().vueInstance.axios.post(`/api/v1/phieu-xuat-kho/admin-sign/${id}`);
  }

  static getDanhSachKhoFromBBGT({
    id_bien_ban,
    loai_dinh_muc,
  }: BbgtKhoListQuery): Promise<{ data: DanhSachKhoBbgtResponse }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/phieu-xuat-kho/bien-ban-giai-the/${id_bien_ban}/danh-sach-kho`,
      {
        params: {
          loai_dinh_muc,
        },
      }
    );
  }

  static getDanhSachVatTuFromBBGT({
    id_bien_ban,
    id_kho,
    loai_dinh_muc,
    exclude_id_phieu_xuat,
  }: BbgtVatTuSelectionQuery): Promise<{ data: DanhSachVatTuBbgtResponse }> {
    let query = `loai_dinh_muc=${loai_dinh_muc}`;

    exclude_id_phieu_xuat?.forEach((id) => {
      query = `${query}&exclude_id_phieu_xuat[]=${id}`;
    });

    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/phieu-xuat-kho/bien-ban-giai-the/${id_bien_ban}/danh-sach-vat-tu/${id_kho}?${query}`
    );
  }
}
